import { useState } from "react";
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  ExternalLink,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Skeleton } from "@/components/ui/Skeleton";
import { Dialog } from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { Field } from "@/components/ui/Label";
import { PageHeader } from "@/components/layout/PageHeader";
import {
  useCompleteCompliance,
  useComplianceItems,
  type ComplianceItem,
  type ComplianceStatus,
} from "@/hooks/useCompliance";
import { daysUntil, formatDate, todayIso } from "@/lib/utils";

export function CompliancePage() {
  const items = useComplianceItems();
  const [filing, setFiling] = useState<ComplianceItem | null>(null);

  const sorted = (items.data ?? [])
    .slice()
    .sort((a, b) => a.dueDate.localeCompare(b.dueDate));

  const open = sorted.filter(
    (c) => c.status !== "Completed" && c.status !== "NotApplicable" && c.status !== "Skipped",
  );
  const filed = sorted.filter((c) => c.status === "Completed");

  return (
    <>
      <PageHeader
        eyebrow="Compliance"
        title="Filings, registrations, deadlines"
        description="BOI/FinCEN, Wisconsin annual report, federal + state quarterly estimates. We surface anything legally required before it bites."
      />

      <h2 className="mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
        Open ({open.length})
      </h2>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        {items.isLoading && (
          <>
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-32 w-full" />
          </>
        )}
        {!items.isLoading && open.length === 0 && (
          <Card>
            <CardContent className="py-6 text-sm text-muted-foreground">
              All caught up. New deadlines surface automatically as the year progresses.
            </CardContent>
          </Card>
        )}
        {open.map((c) => (
          <ComplianceCard key={c.id} item={c} onMarkFiled={() => setFiling(c)} />
        ))}
      </div>

      {filed.length > 0 && (
        <>
          <h2 className="mt-10 mb-3 text-sm font-semibold uppercase tracking-wider text-muted-foreground">
            Filed ({filed.length})
          </h2>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {filed.map((c) => (
              <ComplianceCard key={c.id} item={c} onMarkFiled={() => setFiling(c)} />
            ))}
          </div>
        </>
      )}

      <FileDialog item={filing} onClose={() => setFiling(null)} />
    </>
  );
}

function ComplianceCard({
  item,
  onMarkFiled,
}: {
  item: ComplianceItem;
  onMarkFiled: () => void;
}) {
  const days = daysUntil(item.dueDate);
  const overdue = item.status !== "Completed" && days != null && days < 0;
  const urgent = item.status !== "Completed" && days != null && days >= 0 && days <= item.alertDaysBefore;
  const highlight = overdue || (item.isHighPenalty && item.status !== "Completed");

  return (
    <Card className={highlight ? "border-destructive/50" : undefined}>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex flex-wrap items-center gap-2">
              {item.title}
              {item.isHighPenalty && <Badge tone="danger">High penalty</Badge>}
            </CardTitle>
            <CardDescription>
              {humanCategory(item.category)} · {item.cadence}
            </CardDescription>
          </div>
          <StatusBadge status={item.status} overdue={overdue} />
        </div>
      </CardHeader>
      <CardContent className="space-y-2 text-sm">
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-1 text-muted-foreground">
            <Clock className="h-4 w-4" />
            Due {formatDate(item.dueDate)}
          </span>
          {item.status !== "Completed" && days != null && (
            <Badge tone={overdue ? "danger" : urgent ? "warning" : "neutral"}>
              {overdue ? `${Math.abs(days)}d late` : `${days}d`}
            </Badge>
          )}
        </div>
        {item.description && (
          <p className="text-xs text-muted-foreground">{item.description}</p>
        )}
        {item.status === "Completed" && item.confirmationNumber && (
          <p className="text-xs text-muted-foreground">
            Confirmation: <span className="font-mono">{item.confirmationNumber}</span>
            {item.completedOn && ` · ${formatDate(item.completedOn)}`}
          </p>
        )}
        <div className="flex items-center justify-between pt-2">
          {item.authorityUrl ? (
            <a
              href={item.authorityUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
            >
              Filing portal <ExternalLink className="h-3 w-3" />
            </a>
          ) : (
            <span />
          )}
          {item.status !== "Completed" && (
            <Button size="sm" variant="outline" onClick={onMarkFiled}>
              <CheckCircle2 className="h-4 w-4" />
              Mark filed
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function StatusBadge({ status, overdue }: { status: ComplianceStatus; overdue: boolean }) {
  if (overdue) {
    return (
      <Badge tone="danger">
        <AlertTriangle className="mr-1 h-3 w-3" />
        Overdue
      </Badge>
    );
  }
  switch (status) {
    case "Completed":
      return (
        <Badge tone="success">
          <CheckCircle2 className="mr-1 h-3 w-3" />
          Filed
        </Badge>
      );
    case "InProgress":
      return <Badge tone="info">In progress</Badge>;
    case "NotApplicable":
      return <Badge tone="neutral">N/A</Badge>;
    case "Skipped":
      return <Badge tone="neutral">Skipped</Badge>;
    default:
      return <Badge tone="warning">Upcoming</Badge>;
  }
}

function FileDialog({ item, onClose }: { item: ComplianceItem | null; onClose: () => void }) {
  const complete = useCompleteCompliance();
  const [confirmation, setConfirmation] = useState("");
  const [completedOn, setCompletedOn] = useState(todayIso());
  if (!item) return null;
  return (
    <Dialog
      open={!!item}
      onClose={onClose}
      title={`Mark filed: ${item.title}`}
      description="Record the confirmation number so you can defend the filing if questioned later."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={complete.isPending}
            onClick={async () => {
              await complete.mutateAsync({
                id: item.id,
                completedOn,
                confirmationNumber: confirmation || undefined,
              });
              onClose();
            }}
          >
            {complete.isPending ? "Saving…" : "Save"}
          </Button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Filed on">
          <Input type="date" value={completedOn} onChange={(e) => setCompletedOn(e.target.value)} />
        </Field>
        <Field label="Confirmation #">
          <Input
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
            placeholder="optional"
          />
        </Field>
      </div>
    </Dialog>
  );
}

function humanCategory(c: string) {
  switch (c) {
    case "BoiFinCEN":
      return "BOI / FinCEN";
    case "StateAnnualReport":
      return "State annual report";
    case "EstimatedTaxFederal":
      return "Federal estimated tax";
    case "EstimatedTaxState":
      return "State estimated tax";
    case "Form1099Nec":
      return "1099-NEC";
    case "PayrollFederal":
      return "Federal payroll";
    case "PayrollState":
      return "State payroll";
    case "WorkersComp":
      return "Workers' comp";
    case "BusinessLicense":
      return "Business license";
    default:
      return c;
  }
}
